import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle, Circle, UploadSimple, FileArrowUp, Spinner } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { generateReceipt } from "@/lib/receipt";
import Footer from "@/components/Footer";

const field = "w-full bg-secondary border border-border rounded-sm px-4 py-3 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors placeholder:text-muted-foreground";
const label = "font-mono text-[11px] uppercase tracking-widest text-muted-foreground mb-2 block";
const STEPS = ["Service", "Details", "Documents", "Review"];

export default function Apply() {
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [step, setStep] = useState(0);
  const [selected, setSelected] = useState(null);
  const [uploadedDocs, setUploadedDocs] = useState({}); // { docName: DocumentRef }
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ full_name: "", date_of_birth: "", gender: "", phone: "", email: "", address: "", preferred_date: "", notes: "" });

  useEffect(() => {
    api.get("/services").then(({ data }) => setServices(data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (user) setForm((f) => ({ ...f, full_name: f.full_name || user.name || "", email: f.email || user.email || "", phone: f.phone || user.phone || "" }));
  }, [user]);

  if (ready && !user) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
        <h1 className="font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl">Login to apply</h1>
        <p className="text-muted-foreground mt-4 max-w-sm">Create a free account or log in to start and track your license application.</p>
        <div className="flex gap-4 mt-8">
          <Link to="/login" data-testid="apply-login" className="px-6 py-3 border border-border rounded-sm font-mono text-sm uppercase tracking-widest hover:border-primary transition-colors">Login</Link>
          <Link to="/register" data-testid="apply-register" className="px-6 py-3 bg-primary text-primary-foreground rounded-sm font-mono text-sm uppercase tracking-widest">Register</Link>
        </div>
      </div>
    );
  }

  const uploadDoc = async (docName, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("File too large (max 5MB)");
    setUploadingDoc(docName);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploadedDocs((p) => ({ ...p, [docName]: { name: docName, file_id: data.file_id, storage_path: data.storage_path, filename: data.filename, content_type: data.content_type } }));
      toast.success(`${docName} uploaded`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploadingDoc(null);
    }
  };

  const canNext = () => {
    if (step === 0) return !!selected;
    if (step === 1) return form.full_name && form.date_of_birth && form.gender && form.phone && form.email && form.address;
    if (step === 2) return selected.docs.every((d) => uploadedDocs[d]);
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/applications", {
        ...form, service_type: selected.title, documents: Object.values(uploadedDocs),
      });
      toast.success(`Application ${data.app_id} submitted!`);
      try { generateReceipt(data); } catch (_) { /* ignore pdf errors */ }
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen pt-24">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10">
        <span className="font-mono text-xs uppercase tracking-[0.3em] text-primary">New application</span>
        <h1 className="font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl mt-3">Apply online</h1>

        {/* Stepper */}
        <div className="flex items-center gap-2 mt-10 mb-12">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-2 flex-1">
              <div className={`flex items-center gap-2 ${i <= step ? "text-primary" : "text-muted-foreground"}`}>
                {i < step ? <CheckCircle size={20} weight="fill" /> : <Circle size={20} weight={i === step ? "fill" : "regular"} />}
                <span className="font-mono text-[11px] uppercase tracking-widest hidden sm:inline">{s}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`h-px flex-1 ${i < step ? "bg-primary" : "bg-border"}`} />}
            </div>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.35 }}>
            {step === 0 && (
              <div className="grid sm:grid-cols-2 gap-4">
                {services.map((s) => (
                  <button
                    key={s.slug} data-testid={`apply-service-${s.slug}`}
                    onClick={() => { setSelected(s); setUploadedDocs({}); }}
                    className={`text-left p-6 border rounded-sm transition-colors ${selected?.slug === s.slug ? "border-primary bg-primary/5" : "border-border hover:border-muted-foreground"}`}
                  >
                    <h3 className="font-display font-bold uppercase tracking-tight text-lg">{s.title}</h3>
                    <p className="text-muted-foreground text-sm mt-1">{s.tagline}</p>
                    <p className="font-mono text-[11px] uppercase tracking-widest text-primary mt-3">⏱ {s.duration}</p>
                  </button>
                ))}
              </div>
            )}

            {step === 1 && (
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="sm:col-span-2"><label className={label}>Full name (as on Aadhaar)</label><input data-testid="apply-fullname" className={field} value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} /></div>
                <div><label className={label}>Date of birth</label><input type="date" data-testid="apply-dob" className={field} value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} /></div>
                <div><label className={label}>Gender</label>
                  <select data-testid="apply-gender" className={field} value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })}>
                    <option value="">Select…</option><option>Male</option><option>Female</option><option>Other</option>
                  </select>
                </div>
                <div><label className={label}>Phone</label><input data-testid="apply-phone" className={field} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                <div><label className={label}>Email</label><input type="email" data-testid="apply-email" className={field} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div className="sm:col-span-2"><label className={label}>Full address</label><textarea rows={3} data-testid="apply-address" className={field} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
                <div><label className={label}>Preferred date (optional)</label><input type="date" data-testid="apply-preferred" className={field} value={form.preferred_date} onChange={(e) => setForm({ ...form, preferred_date: e.target.value })} /></div>
              </div>
            )}

            {step === 2 && (
              <div>
                <p className="text-muted-foreground text-sm mb-6">Upload a clear photo or scan of each document for <span className="text-primary">{selected.title}</span>. JPG, PNG or PDF up to 5MB.</p>
                <div className="space-y-3">
                  {selected.docs.map((d) => {
                    const uploaded = uploadedDocs[d];
                    const busy = uploadingDoc === d;
                    const id = d.replace(/\s+/g, "-").toLowerCase();
                    return (
                      <div key={d} className={`flex items-center gap-4 p-4 border rounded-sm transition-colors ${uploaded ? "border-primary bg-primary/5" : "border-border"}`}>
                        {uploaded ? <CheckCircle size={24} weight="fill" className="text-primary shrink-0" /> : <FileArrowUp size={24} className="text-muted-foreground shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm">{d}</p>
                          {uploaded && <p className="font-mono text-[10px] text-muted-foreground truncate">{uploaded.filename}</p>}
                        </div>
                        <label className={`shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-sm font-mono text-[10px] uppercase tracking-widest cursor-pointer transition-colors ${uploaded ? "border border-border hover:border-primary" : "bg-primary text-primary-foreground hover:bg-primary/90"}`}>
                          {busy ? <Spinner size={14} className="animate-spin" /> : <UploadSimple size={14} />}
                          {busy ? "Uploading" : uploaded ? "Replace" : "Upload"}
                          <input type="file" accept="image/*,application/pdf" className="hidden" data-testid={`apply-doc-${id}`} disabled={busy}
                            onChange={(e) => uploadDoc(d, e.target.files?.[0])} />
                        </label>
                      </div>
                    );
                  })}
                </div>
                <textarea rows={3} data-testid="apply-notes" className={`${field} mt-6`} placeholder="Additional notes (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
            )}

            {step === 3 && (
              <div className="bg-card border border-border rounded-sm p-7 space-y-4">
                <Row k="Service" v={selected.title} />
                <Row k="Full name" v={form.full_name} />
                <Row k="Date of birth" v={form.date_of_birth} />
                <Row k="Gender" v={form.gender} />
                <Row k="Phone" v={form.phone} />
                <Row k="Email" v={form.email} />
                <Row k="Address" v={form.address} />
                <Row k="Documents" v={Object.keys(uploadedDocs).join(", ") || "None uploaded"} />
                {form.notes && <Row k="Notes" v={form.notes} />}
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="flex justify-between mt-10">
          <button
            data-testid="apply-back" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}
            className="inline-flex items-center gap-2 px-5 py-3 border border-border rounded-sm font-mono text-xs uppercase tracking-widest disabled:opacity-40 hover:border-muted-foreground transition-colors"
          >
            <ArrowLeft size={16} /> Back
          </button>
          {step < 3 ? (
            <button
              data-testid="apply-next" onClick={() => canNext() ? setStep((s) => s + 1) : toast.error("Please complete this step")}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-sm font-mono text-xs uppercase tracking-widest hover:bg-primary/90 transition-colors"
            >
              Continue <ArrowRight size={16} />
            </button>
          ) : (
            <button
              data-testid="apply-submit" onClick={submit} disabled={submitting}
              className="inline-flex items-center gap-2 px-8 py-3 bg-primary text-primary-foreground rounded-sm font-mono text-xs uppercase tracking-widest hover:bg-primary/90 transition-colors disabled:opacity-60"
            >
              {submitting ? "Submitting…" : "Submit application"}
            </button>
          )}
        </div>
      </div>
      <Footer />
    </main>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-6 border-b border-border pb-3 last:border-0">
      <span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground shrink-0">{k}</span>
      <span className="text-sm text-right">{v}</span>
    </div>
  );
}
